import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'

import * as assistant from '../assistant'

type CacheForGroup = Map<string, assistant.conversational.Group>

function getAgentIdsKey(
  agents: readonly ents.core.agent.Display[],
): string {
  return agents.map(agent => agent.id).join('|')
}

/**
 * Instantiates the concrete frontend group and memoizes it for later reads.
 */
function createGroup(
  groupMeta: ents.core.agent.Display.Group,
  injector: ngCore.Injector,
): assistant.conversational.Group {
  const assistantDisplays = groupMeta.agents as readonly [
    ents.core.agent.Display,
    ...ents.core.agent.Display[],
  ]
  const group = new assistant.conversational.Group({
    assistantDisplays,
    id: groupMeta.id,
    injector,
  })

  return group
}

/**
 * Creates frontend assistant groups from entity-owned display groups.
 *
 * This covers agents that use the standard conversational UI.
 */
@ngCore.Injectable({ providedIn: 'root' })
export class Groups {
  private readonly _injector = ngCore.inject(ngCore.Injector)

  /**
   * Groups are primarily cached by shared entity group ID.
   *
   * The nested cache key represents the assigned-agent subset. A grouped
   * assistant such as MosaicTreeEditor can be instantiated with only orgChart,
   * only projectRecommendation, or both, depending on user assignment.
   */
  private readonly _groups = new Map<
    ents.core.agent.Display.Group.Id,
    CacheForGroup
  >()

  /**
   * Returns a conversational group for a visible assigned entity group.
   */
  public get(
    group: ents.core.agent.Display.Group,
  ): assistant.conversational.Group | undefined {
    if (!group.agents.length) {
      return undefined
    }

    const groupCache =
      this._groups.get(group.id) ??
      new Map<string, assistant.conversational.Group>()
    const agentIdsKey = getAgentIdsKey(group.agents)

    this._groups.set(group.id, groupCache)

    const assistantGroup =
      groupCache.get(agentIdsKey) ??
      ngCore.untracked(() =>
        ngCore.runInInjectionContext(this._injector, () =>
          createGroup(group, this._injector),
        ),
      )

    groupCache.set(agentIdsKey, assistantGroup)

    return assistantGroup
  }

  /**
   * Returns a conversational group for a known entity display group ID.
   *
   * This is useful for feature pages that intentionally use hidden/internal
   * assistant groups without exposing those groups in the assistant launcher.
   */
  public getById(
    groupId: ents.core.agent.Display.Group.Id,
  ): assistant.conversational.Group {
    const group = ents.core.agent.Display.Group.get(groupId)

    if (!group) {
      throw new Error(`No assistant display group registered for "${groupId}".`)
    }

    const assistantGroup = this.get(group)

    if (!assistantGroup) {
      throw new Error(`Assistant display group "${groupId}" has no agents.`)
    }

    return assistantGroup
  }
}
